import { useTranslation } from 'react-i18next';

import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import { LimitWindowsCard } from 'features/usage-config/components/limit-windows-card';
import { EnforcementSwitchCard } from 'features/usage-config/components/enforcement-switch-card';
import { ModelCategoriesCard } from 'features/usage-config/components/model-categories-card';
import { ModelRestrictionCard } from 'features/usage-config/components/model-restriction-card';
import { SettlementCard } from 'features/usage-config/components/settlement-card';
import { UsageMetersCard } from 'features/usage-config/components/usage-meters-card';
import {
  buildTenantMetadata,
  buildTenantPayload,
  draftFromConfig,
  emptyConfig,
  withLimitWindows,
} from 'features/usage-config/components/usage-config-draft';
import { useUsageConfigDraft } from 'features/usage-config/components/use-usage-config-draft';
import { USAGE_LIMITS_I18N } from 'features/usage-config/constants/usage-meters';
import {
  hasKeyProblem,
  keyProblems,
  KeyProblem,
} from 'features/usage-config/components/usage-keys';
import { notifyFailed, notifySaved } from 'features/usage-config/components/usage-toasts';
import {
  useSaveUsageEnforcement,
  useUsageEnforcement,
} from 'features/usage-config/hooks/use-usage-enforcement';

const T = USAGE_LIMITS_I18N;

const LoadingSkeleton = () => (
  <div className="rounded-sm border bg-card px-2 py-2 shadow-sm">
    <div className="space-y-4 px-2 py-4">
      <Skeleton className="h-5 w-48" />
      {Array.from({ length: 3 }).map((_item, index) => (
        <Skeleton key={index} className="h-10 w-full" />
      ))}
    </div>
  </div>
);

export const UsageConfigCard = () => {
  const { t } = useTranslation();
  const { data: config, isLoading, isError } = useUsageEnforcement();
  const saveEnforcement = useSaveUsageEnforcement();
  // The whole tenant: a tenant-wide payer can be anyone in it.
  const { draft, setDraft, discard, isDirty } = useUsageConfigDraft(config);

  const shown = draft ?? draftFromConfig(emptyConfig());
  const readOnly = !config;
  const dimmed = !shown.enforcementEnabled;

  // Windows and categories are checked together: they share one namespace.
  const problems = keyProblems(
    shown.limitWindows.map((limitWindow) => limitWindow.key),
    shown.modelCategories.map((category) => category.key)
  );
  const keyBlocked = hasKeyProblem(problems);
  const messageFor = (problem: KeyProblem) =>
    problem === 'empty' ? t(T.KEY_EMPTY) : problem === 'taken' ? t(T.KEY_TAKEN) : '';

  const submit = () => {
    if (!draft || readOnly || keyBlocked) return;
    saveEnforcement.mutate(
      { payload: buildTenantPayload(draft), metadata: buildTenantMetadata(draft) },
      {
        onSuccess: () => notifySaved(t(T.SAVED)),
        onError: (error) => notifyFailed(t(T.SAVE_FAILED), error),
      }
    );
  };

  if (isLoading) return <LoadingSkeleton />;

  return (
    <div className="flex flex-col gap-4">
      {isError && (
        <p className="rounded-sm bg-muted px-3 py-2 text-sm text-destructive">{t(T.LOAD_FAILED)}</p>
      )}

      {config?.is_saved === false && (
        <p className="text-xs text-muted-foreground">{t(T.DEFAULTS_NOTE)}</p>
      )}

      <EnforcementSwitchCard
        description={t(T.DESCRIPTION)}
        enforcing={shown.enforcementEnabled}
        onEnforcingChange={(enforcementEnabled) =>
          setDraft((current) => (current ? { ...current, enforcementEnabled } : current))
        }
        showOffNote={!shown.enforcementEnabled}
        readOnly={readOnly}
      />

      <UsageMetersCard
        meters={shown.meters}
        enforcementEnabled={shown.enforcementEnabled}
        onMeterChange={(meterKey, enabled) =>
          setDraft((current) =>
            current
              ? {
                  ...current,
                  meters: {
                    ...current.meters,
                    [meterKey]: { ...current.meters[meterKey], enabled },
                  },
                }
              : current
          )
        }
        hint={t(T.SETUP_HINT)}
        dimmed={dimmed}
        readOnly={readOnly}
      />

      <LimitWindowsCard
        windows={shown.limitWindows}
        onWindowsChange={(limitWindows) =>
          setDraft((current) => (current ? withLimitWindows(current, limitWindows) : current))
        }
        dimmed={dimmed}
        readOnly={readOnly}
        reservedKeys={shown.modelCategories.map((category) => category.key)}
        keyErrors={problems.windows.map(messageFor)}
      />

      <ModelRestrictionCard
        restriction={shown.modelRestriction}
        onRestrictionChange={(modelRestriction) =>
          setDraft((current) => (current ? { ...current, modelRestriction } : current))
        }
        readOnly={readOnly}
      />

      <ModelCategoriesCard
        enabled={shown.modelRestrictionsEnabled}
        onEnabledChange={(modelRestrictionsEnabled) =>
          setDraft((current) => (current ? { ...current, modelRestrictionsEnabled } : current))
        }
        categories={shown.modelCategories}
        onCategoriesChange={(modelCategories) =>
          setDraft((current) => (current ? { ...current, modelCategories } : current))
        }
        windows={shown.limitWindows}
        restriction={shown.modelRestriction}
        organizationId=""
        dimmed={dimmed}
        readOnly={readOnly}
        keyErrors={problems.categories.map(messageFor)}
      />

      <SettlementCard
        settlement={shown.settlement}
        onSettlementChange={(settlement) =>
          setDraft((current) => (current ? { ...current, settlement } : current))
        }
        dimmed={dimmed}
        readOnly={readOnly}
      />

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={discard} disabled={!isDirty || saveEnforcement.isPending}>
          {t(T.DISCARD)}
        </Button>
        <Button
          onClick={submit}
          disabled={!isDirty || saveEnforcement.isPending || readOnly || keyBlocked}
          title={keyBlocked ? t(T.KEY_BLOCKS_SAVE) : undefined}
        >
          {t(T.SAVE)}
        </Button>
      </div>
    </div>
  );
};
